import { FlagWrapper, FlagBody, FlagComponent } from '@jsluna/grid';
import { CloseButton, CancelButton } from '@jsluna/button';
import { Manager, Reference, Popper } from 'react-popper';
import { childMatchesType } from '@jsluna/utils';
import { InfoCircle, Info } from '@jsluna/icons';
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var AlertIcon = function AlertIcon(_ref) {
  var children = _ref.children,
      className = _ref.className;
  var element = React.Children.only(children);
  return /*#__PURE__*/React.cloneElement(element, {
    className: classnames('ln-c-alert__icon', className, element.props && element.props.className)
  });
};

AlertIcon.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};
AlertIcon.defaultProps = {
  children: undefined,
  className: undefined
};
AlertIcon.displayName = 'AlertIcon';
var _excluded = ["element", "children", "className", "type", "error", "warning", "success", "onClose", "closeButtonElement", "open", "squared"];

var Alert = function Alert(_ref) {
  var _classnames;

  var element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      type = _ref.type,
      error = _ref.error,
      warning = _ref.warning,
      success = _ref.success,
      onClose = _ref.onClose,
      closeButtonElement = _ref.closeButtonElement,
      open = _ref.open,
      squared = _ref.squared,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);

  var Element = element;
  var hasIcon = false;
  React.Children.forEach(children, function (child) {
    if (child && child.type && child.type === AlertIcon) {
      hasIcon = true;
    }
  });
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    className: classnames('ln-c-alert', (_classnames = {}, _classnames["ln-c-alert--" + type] = type, _classnames['ln-c-alert--error'] = error, _classnames['ln-c-alert--warning'] = warning, _classnames['ln-c-alert--success'] = success, _classnames['ln-c-alert--dismissable'] = onClose, _classnames['ln-c-alert--squared'] = squared, _classnames['ln-u-hidden'] = open === false && onClose, _classnames), className),
    role: "alert"
  }), hasIcon ? /*#__PURE__*/React.createElement(FlagWrapper, null, /*#__PURE__*/React.createElement(FlagComponent, {
    alignment: "top",
    className: "ln-u-margin-right"
  }, React.Children.map(children, function (child) {
    return child && child.type && child.type === AlertIcon && child;
  })), /*#__PURE__*/React.createElement(FlagBody, null, React.Children.map(children, function (child) {
    return child && (!child.type || child.type !== AlertIcon) && child;
  }))) : children, onClose && /*#__PURE__*/React.createElement(CloseButton, {
    element: closeButtonElement,
    label: "Dismiss message",
    onClick: onClose,
    className: "ln-c-alert__dismiss-button"
  }));
};

Alert.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
  className: PropTypes.string,
  type: PropTypes.string,
  error: PropTypes.bool,
  warning: PropTypes.bool,
  success: PropTypes.bool,
  onClose: PropTypes.func,
  closeButtonElement: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  open: PropTypes.bool,
  squared: PropTypes.bool
};
Alert.defaultProps = {
  element: 'div',
  children: undefined,
  className: undefined,
  type: undefined,
  error: false,
  warning: false,
  success: false,
  onClose: undefined,
  closeButtonElement: undefined,
  open: undefined,
  squared: false
};
Alert.displayName = 'Alert';
var _excluded$1 = ["children", "element", "icon", "dismissible", "onClose", "disabled", "outlined", "className", "selected"];

var Tag = function Tag(_ref) {
  var children = _ref.children,
      element = _ref.element,
      icon = _ref.icon,
      dismissible = _ref.dismissible,
      onClose = _ref.onClose,
      disabled = _ref.disabled,
      outlined = _ref.outlined,
      className = _ref.className,
      selected = _ref.selected,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$1);

  var baseClassName = 'ln-c-tag__beta';
  var Element = element;
  return /*#__PURE__*/React.createElement(Element, _extends({
    className: classnames(baseClassName, outlined && baseClassName + "--outlined", dismissible && baseClassName + "--dismissible", icon && baseClassName + "--has-icon", disabled && 'is-disabled', selected && 'selected', className)
  }, props, {
    "aria-describedby": "tag-body"
  }), icon && /*#__PURE__*/React.createElement("span", {
    className: "ln-c-tag--icon ln-u-soft-right-xs"
  }, icon), /*#__PURE__*/React.createElement("span", {
    className: classnames('ln-c-tag--body'),
    id: "tag-body"
  }, children), dismissible && /*#__PURE__*/React.createElement(CancelButton, {
    className: classnames('ln-c-tag--button', disabled && 'is-disabled'),
    role: "button",
    onClick: onClose,
    element: "div"
  }));
};

Tag.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,

  /** If set to true this adds a button to close the element */
  dismissible: PropTypes.bool,

  /** Allows the top-level element to be customized */
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),

  /** Allows the element to feature a Luna icon */
  icon: PropTypes.node,

  /** Add a function to close/hide the tag */
  onClose: PropTypes.func,

  /** Uses the outlined tag style */
  outlined: PropTypes.bool,
  selected: PropTypes.bool
};
Tag.defaultProps = {
  className: undefined,
  disabled: false,
  dismissible: false,
  element: 'div',
  icon: undefined,
  onClose: undefined,
  outlined: false,
  selected: false
};
var _excluded$2 = ["children", "className", "element", "outlined", "onClick", "tooltipId", "label"];

var TooltipIcon = function TooltipIcon(_ref) {
  var children = _ref.children,
      className = _ref.className,
      element = _ref.element,
      outlined = _ref.outlined,
      onClick = _ref.onClick,
      tooltipId = _ref.tooltipId,
      label = _ref.label,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded$2);

  var Element = element;
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    className: classnames('ln-c-tooltip__icon', className),
    onClick: onClick,
    "aria-label": label,
    "aria-controls": tooltipId
  }), children || (outlined ? /*#__PURE__*/React.createElement(Info, {
    size: "large"
  }) : /*#__PURE__*/React.createElement(InfoCircle, {
    size: "large"
  })));
};

TooltipIcon.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.objectOf]),
  children: PropTypes.node,
  className: PropTypes.string,
  label: PropTypes.string,
  outlined: PropTypes.bool,
  onClick: PropTypes.func,
  tooltipId: PropTypes.string
};
TooltipIcon.defaultProps = {
  element: 'button',
  children: undefined,
  className: undefined,
  label: 'More information',
  outlined: false,
  onClick: function onClick(e) {
    return e.preventDefault();
  },
  tooltipId: undefined
};
TooltipIcon.displayName = 'TooltipIcon';
var _excluded$3 = ["children", "element", "innerRef", "className", "arrow"];

var TooltipMessage = function TooltipMessage(_ref) {
  var children = _ref.children,
      element = _ref.element,
      innerRef = _ref.innerRef,
      className = _ref.className,
      arrow = _ref.arrow,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded$3);

  var Element = element;
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    className: classnames('ln-c-tooltip__message', className),
    ref: innerRef
  }), children, arrow);
};

TooltipMessage.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.objectOf]),
  children: PropTypes.node,
  className: PropTypes.string,

  /** The arrow element with positioning applied */
  arrow: PropTypes.element,

  /** Ref callback for message element */
  innerRef: PropTypes.func
};
TooltipMessage.defaultProps = {
  element: 'div',
  children: undefined,
  className: undefined,
  arrow: undefined,
  innerRef: undefined
};
TooltipMessage.displayName = 'TooltipMessage';
var _excluded$4 = ["open", "element", "children", "className", "tooltipId", "position", "priority", "boundariesElement", "escapeWithReference", "popper", "outlined", "large", "label"];

var Tooltip = function Tooltip(_ref) {
  var open = _ref.open,
      element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      tooltipId = _ref.tooltipId,
      position = _ref.position,
      priority = _ref.priority,
      boundariesElement = _ref.boundariesElement,
      escapeWithReference = _ref.escapeWithReference,
      popper = _ref.popper,
      outlined = _ref.outlined,
      large = _ref.large,
      label = _ref.label,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded$4);

  var Element = element;
  var modifiers = Object.assign({
    offset: {
      offset: '0, 10px'
    },
    preventOverflow: {
      enabled: true,
      escapeWithReference: escapeWithReference,
      boundariesElement: boundariesElement,
      priority: priority
    }
  }, popper.modifiers);
  var filteredChildren = [];
  var message;
  React.Children.forEach(children, function (child, index) {
    if (childMatchesType(child, TooltipMessage)) {
      message = child;
    } else if (childMatchesType(child, TooltipIcon)) {
      filteredChildren.push( /*#__PURE__*/React.cloneElement(child, {
        outlined: outlined,
        tooltipId: tooltipId,
        label: label,
        key: child && child.props && child.props.id || index
      }));
    } else {
      filteredChildren.push(child);
    }
  });
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    className: classnames('ln-c-tooltip', large && 'ln-c-tooltip--large', outlined && 'ln-c-tooltip--outlined', open && 'is-open')
  }), /*#__PURE__*/React.createElement(Manager, null, /*#__PURE__*/React.createElement(Reference, null, function (_ref2) {
    var ref = _ref2.ref;
    return /*#__PURE__*/React.createElement("div", {
      ref: ref
    }, filteredChildren);
  }), /*#__PURE__*/React.createElement("div", {
    "aria-live": "polite",
    "aria-atomic": "true",
    id: tooltipId,
    role: "tooltip"
  }, open && /*#__PURE__*/React.createElement(Popper, _extends({}, popper, {
    placement: position,
    modifiers: modifiers
  }), function (_ref3) {
    var ref = _ref3.ref,
        style = _ref3.style,
        placement = _ref3.placement,
        arrowProps = _ref3.arrowProps;
    return /*#__PURE__*/React.cloneElement(message, {
      innerRef: ref,
      style: style,
      arrow: /*#__PURE__*/React.createElement("span", {
        ref: arrowProps.ref,
        style: arrowProps.style,
        className: classnames('ln-c-tooltip__arrow', placement && "ln-c-tooltip__arrow--" + placement)
      })
    });
  }))));
};

Tooltip.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.objectOf]),

  /** Show/Hide the Tooltip Message part of the component */
  open: PropTypes.bool,

  /** A unique identifier to enable screenreaders to pair the aria-describedby attribute and the tooltip message id */
  tooltipId: PropTypes.string,

  /** Initial positioning of the tooltip message. Can be positioned at the start, middle or end and left, right, top and bottom  */
  position: PropTypes.string,

  /** Provide a boundry for the position against, can be `scrollParent`, `window`, `viewport` or any DOM element */
  boundariesElement: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),

  /** If set to true this allows the tooltip message to overflow over any set boundries */
  escapeWithReference: PropTypes.bool,

  /** Popper JS overrides */
  popper: PropTypes.shape({
    modifiers: PropTypes.object
  }),

  /** Use the outlined tooltip style */
  outlined: PropTypes.bool,

  /** Set the edge detection priority with popper */
  priority: PropTypes.arrayOf(PropTypes.string),

  /** Size modifier to allow for larger width of message container */
  large: PropTypes.bool,

  /** Label to decribe the trigger element, primarly used by screenreaders. */
  label: PropTypes.string
};
Tooltip.defaultProps = {
  element: 'div',
  children: undefined,
  className: undefined,
  open: false,
  tooltipId: undefined,
  position: 'bottom',
  boundariesElement: 'scrollParent',
  escapeWithReference: false,
  priority: ['top', 'bottom', 'left', 'right'],
  popper: {},
  outlined: false,
  large: false,
  label: undefined
};
Tooltip.displayName = 'Tooltip';
export { Alert, AlertIcon, Tag, Tooltip, TooltipMessage, TooltipIcon };